/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// formik
import { Formik } from 'formik';
import * as Yup from 'yup';
// @material-ui/icons
import Erase from '@material-ui/icons/Delete';
// @material-ui/core components
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import withStyles from '@material-ui/core/styles/withStyles';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import validationFormsStyle from 'assets/jss/material-dashboard-pro-react/views/validationFormsStyle';
// core components
import Card from 'components/UI/Card/Card';
import GridItem from 'components/UI/Grid/GridItem';
import CardBody from 'components/UI/Card/CardBody';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import CustomInput from 'components/UI/CustomInput/CustomInput';
// global variables
import {
  processorRulesActions,
  processorRulesType,
  processorRulesStatus,
} from 'config';
// our components
import { PROCESSOR_RULE } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import Loading from 'components/common/Loading';
import ModalConfirmation from 'components/common/ModalConfirmation';
import WithPermission from 'components/User/WithPermission';

/**
 * @description
 * Validation form's rules.
 * @param {Function} t - Translation function from HOC.
 * @returns {Object} - YUP shape.
 */
const translateValidationSchema = t =>
  Yup.object({
    name: Yup.string().required(t('errors.required')),
    ruleType: Yup.string().required(t('errors.required')),
    actions: Yup.string().required(t('errors.required')),
    triggerFields: Yup.string().test(
      'is-json-string-array',
      t('errors.invalid_json'),
      value => {
        if (!value || value.trim() === '') {
          return true;
        }
        try {
          const parsedValue = JSON.parse(value);
          return (
            Array.isArray(parsedValue) &&
            parsedValue.every(item => typeof item === 'string')
          );
        } catch (error) {
          return false;
        }
      },
    ),
  });

/**
 * Initial values to ProcessorRuleForm
 */
const initialValues = {
  name: '',
  ruleType: processorRulesType.ALLOW,
  actions: [processorRulesActions.INSERT],
  group: '',
  params: [],
  triggerFields: '[]',
};

/**
 * @description Normalize trigger fields from api payload.
 * @param {Array|string} triggerFields - Trigger fields as array or JSON string.
 * @returns {Array} - Trigger fields array.
 */
const normalizeTriggerFields = triggerFields => {
  if (Array.isArray(triggerFields)) {
    return triggerFields;
  }

  if (typeof triggerFields === 'string' && triggerFields.trim() !== '') {
    try {
      const parsedTriggerFields = JSON.parse(triggerFields);
      return Array.isArray(parsedTriggerFields) ? parsedTriggerFields : [];
    } catch (error) {
      return [];
    }
  }

  return [];
};

/**
 * @description Processor rule form component.
 * @augments React.Component
 */
class ProcessorRuleForm extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);

    if (props.editMode) {
      const {
        __typename,
        group,
        params,
        status,
        target,
        ...restValues
      } = props.processorRule;
      this.state = {
        fields: {
          ...restValues,
          group: group || undefined,
          params: this.getCorrectParamsFromEditMode(),
          triggerFields: JSON.stringify(
            normalizeTriggerFields(props.processorRule.triggerFields),
            null,
            2,
          ),
        },
        showDeleteProcessorRule: false,
      };
    } else {
      this.state = {
        fields: {
          ...initialValues,
        },
        showDeleteProcessorRule: false,
      };
    }

    this.formikRef = null;
  }

  /**
   * @description
   * Handle redirect of cancel btn.
   */
  handleCancelBtn = () => {
    const history = this.context;
    const { routePaths } = this.props;
    history.push(routePaths.LIST);
  };

  /**
   * @description
   * Set server errors with setErrors function of Formik.
   * @param {*} serverErrors - Api errors.
   */
  setServerErrors = serverErrors => {
    let all = null;
    if (this.formikRef && serverErrors.length) {
      const errors = {};

      serverErrors.forEach(item => {
        errors[item.field] = item.messages;
        if (item.field === '__all__') {
          all = item.messages;
        }
      });

      setTimeout(() => this.formikRef && this.formikRef.setErrors(errors), 0);
    }
    return all;
  };

  /**
   * @description
   * Return true if params actions combination is valid.
   * @param {Object} params - Rules params.
   * @returns {boolean} - True or False if is valid or not.
   */
  areValidParams = params =>
    !params.some(actionParam =>
      actionParam.values.some(
        ruleParam => ruleParam.value === undefined || ruleParam.value === '',
      ),
    );

  /**
   * @description
   * Return params from actions combination.
   * @param {Object} object - Rule name and action selected.
   * @returns {Array} - Params from action combination.
   */
  getParamsFromAction = ({ ruleName, actionsSelected, params }) => {
    const { data } = this.props;
    const finalParams = [];
    const values = data.processorRuleValidParameters
      .find(rules => rules.name === ruleName)
      .parameters.map(param => ({ key: param, value: '' }));
    actionsSelected.forEach(actionSelected => {
      switch (actionSelected) {
        case processorRulesActions.INSERT: {
          const currentInsertParams = params.find(
            param => param.action === processorRulesActions.INSERT,
          );
          finalParams.push({
            action: processorRulesActions.INSERT,
            values: currentInsertParams ? currentInsertParams.values : values,
          });
          break;
        }
        case processorRulesActions.UPDATE: {
          const currentUpdateParams = params.find(
            param => param.action === processorRulesActions.UPDATE,
          );
          finalParams.push({
            action: processorRulesActions.UPDATE,
            values: currentUpdateParams ? currentUpdateParams.values : values,
          });
          break;
        }
        case processorRulesActions.FLAGGED: {
          const currentFlaggedParams = params.find(
            param => param.action === processorRulesActions.FLAGGED,
          );
          finalParams.push({
            action: processorRulesActions.FLAGGED,
            values: currentFlaggedParams ? currentFlaggedParams.values : values,
          });
          break;
        }
        default:
          break;
      }
    });
    return finalParams;
  };

  /**
   * @description
   * Return correct params in edit mode after merge current with valid params
   * remove __typename
   * @returns {Array} - Correct params.
   */
  getCorrectParamsFromEditMode = () => {
    const { processorRule, data } = this.props;
    // eslint-disable-next-line no-unused-vars
    const { name, __typename, params, ...restValues } = processorRule;

    const validRuleParams = data.processorRuleValidParameters.find(
      rules => rules.name === name,
    ).parameters;

    const paramsWithoutTypename = params.map(
      // eslint-disable-next-line no-unused-vars
      ({ __typename: paramsTypeName, action, values }) => ({
        action,
        values: this.mergeValidRuleParamsWithSavedParamValues(
          validRuleParams,
          values,
        ),
      }),
    );

    return paramsWithoutTypename;
  };

  /**
   * @description
   * Return merged params from valid params to saved params.
   *
   * @param {Array} validRuleParams - Valid params for specific rule.
   * @param {Array} currentParamValues - Current params value.
   * @returns {Array} - Merged params.
   */
  mergeValidRuleParamsWithSavedParamValues = (
    validRuleParams,
    currentParamValues,
  ) => {
    const mergedParamValues = [];
    validRuleParams.forEach(param => {
      const element = currentParamValues.find(value => value.key === param);
      if (element) {
        mergedParamValues.push({ key: element.key, value: element.value });
      } else mergedParamValues.push({ key: param, value: '' });
    });
    return mergedParamValues;
  };

  /**
   * @description
   * To show modal.
   */
  handleCloseOrShowDeleteConfirmation = () => {
    const { showDeleteProcessorRule } = this.state;
    this.setState({ showDeleteProcessorRule: !showDeleteProcessorRule });
  };

  /**
   * @description
   * To show modal.
   */
  handleConfirmDeleteConfirmation = () => {
    const { showDeleteProcessorRule } = this.state;
    const { processorRuleDelete } = this.props;

    this.setState({ showDeleteProcessorRule: !showDeleteProcessorRule });
    processorRuleDelete();
  };

  /**
   * @description Format form values for processor rule mutations.
   * @param {Object} values - Current form values.
   * @returns {Object} - Formatted values for submit.
   */
  formatValuesToSubmit = values => ({
    ...values,
    triggerFields:
      values.triggerFields && values.triggerFields.trim() !== ''
        ? JSON.stringify(JSON.parse(values.triggerFields))
        : '[]',
  });

  /**
   * @description
   * Render Ad form.
   * @returns {*} - No returns value.
   */
  render() {
    const {
      classes,
      errors: serverErrors,
      editMode,
      submitLoading,
      processorRuleSave,
      data,
      processorRule,
      closeNotification,
      t,
    } = this.props;
    const { fields, showDeleteProcessorRule } = this.state;

    const all = this.setServerErrors(serverErrors);
    const validationSchema = translateValidationSchema(t);
    return (
      <React.Fragment>
        <Snackbars
          place="tc"
          message={all || ''}
          open={!!all}
          color="danger"
          close
          closeNotification={closeNotification}
        />
        <ModalConfirmation
          show={showDeleteProcessorRule}
          confirm={editMode ? this.handleConfirmDeleteConfirmation : () => {}}
          cancel={this.handleCloseOrShowDeleteConfirmation}
          title={<span>{t('processor_rule.form.delete_confirmation')}</span>}
          description={fields.name}
        />
        <Formik
          ref={ref => {
            this.formikRef = ref;
          }}
          initialValues={fields}
          validationSchema={validationSchema}
          onSubmit={values => {
            if (this.areValidParams(values.params)) {
              processorRuleSave(this.formatValuesToSubmit(values));
            } else
              this.formikRef.setFieldError(
                'params',
                t('errors.invalid_params'),
              );
          }}
          render={({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
          }) => (
            <GridItem className={classes.cardContainer}>
              <div
                className={`${classes.row} ${classes.contentBtns}`}
                style={{ justifyContent: 'space-between', display: 'flex' }}
              >
                <h3>
                  {editMode
                    ? t('processor_rule.form.edit')
                    : t('processor_rule.form.create')}
                </h3>
                {editMode &&
                  processorRule.status !== processorRulesStatus.DELETED && (
                    <WithPermission allowPermissions={['change_processorrule']}>
                      <Button
                        id="erase-processor-rule"
                        simple
                        color="danger"
                        onClick={this.handleCloseOrShowDeleteConfirmation}
                      >
                        <Erase /> {t('processor_rule.form.erase')}
                      </Button>
                    </WithPermission>
                  )}
              </div>
              <form onSubmit={handleSubmit}>
                <Card>
                  <Loading show={submitLoading} />
                  <CardBody>
                    <GridContainer direction="column" alignItems="center">
                      <GridItem xs={12} sm={10}>
                        <FormControl
                          fullWidth
                          className={classes.selectFormControl}
                        >
                          <InputLabel
                            htmlFor="name"
                            className={classes.selectLabel}
                          >
                            {t('processor_rule.form.fields.name')}
                          </InputLabel>
                          <Select
                            id="name"
                            value={values.name}
                            disabled={editMode}
                            onChange={e => {
                              handleChange(e);
                              if (values.actions.length !== 0) {
                                const params = this.getParamsFromAction({
                                  ruleName: e.target.value,
                                  actionsSelected: values.actions,
                                  params: [],
                                });
                                handleChange({
                                  target: {
                                    name: 'params',
                                    id: 'params',
                                    value: params,
                                  },
                                });
                              }
                            }}
                            MenuProps={{ className: classes.selectMenu }}
                            inputProps={{
                              name: 'name',
                              id: 'name',
                            }}
                          >
                            {data.processorRuleValidParameters.map(item => (
                              <MenuItem
                                key={item.name}
                                classes={{
                                  root: classes.selectMenuItem,
                                  selected: classes.selectMenuItemSelected,
                                }}
                                value={item.name}
                              >
                                {item.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {touched.name && Boolean(errors.name) && (
                          <p className={classes.errorSpecialFields}>
                            {touched.name ? errors.name : ''}
                          </p>
                        )}
                        <FormControl
                          fullWidth
                          className={classes.selectFormControl}
                        >
                          <CustomInput
                            labelText={t('processor_rule.form.fields.group')}
                            id="group"
                            data-cy="processorRuleGroup"
                            formControlProps={{
                              fullWidth: true,
                              style: { margin: '0px' },
                            }}
                            helpText={touched.group ? errors.group : ''}
                            error={touched.group && Boolean(errors.group)}
                            inputProps={{
                              value: values.group,
                              onChange: e => {
                                handleChange(e);
                              },
                              onBlur: handleBlur,
                              type: 'text',
                            }}
                          />
                        </FormControl>
                        <FormControl
                          fullWidth
                          className={classes.selectFormControl}
                        >
                          <CustomInput
                            labelText={t(
                              'processor_rule.form.fields.trigger_fields',
                            )}
                            id="triggerFields"
                            data-cy="processorRuleTriggerFields"
                            formControlProps={{
                              fullWidth: true,
                              style: { margin: '0px' },
                            }}
                            helpText={
                              touched.triggerFields ? errors.triggerFields : ''
                            }
                            error={
                              touched.triggerFields &&
                              Boolean(errors.triggerFields)
                            }
                            inputProps={{
                              value: values.triggerFields,
                              onChange: handleChange,
                              onBlur: handleBlur,
                              type: 'text',
                              multiline: true,
                              rows: 4,
                            }}
                          />
                        </FormControl>
                        <FormControl
                          fullWidth
                          className={classes.selectFormControl}
                        >
                          <span>
                            {t('processor_rule.form.fields.ruleType')}
                          </span>
                          <div className={classes.buttonGroup}>
                            <Button
                              data-cy="allowRuleType"
                              color="info"
                              round
                              className={classes.firstButton}
                              simple={
                                values.ruleType !== processorRulesType.ALLOW
                              }
                              onClick={() =>
                                handleChange({
                                  target: {
                                    name: 'ruleType',
                                    value: processorRulesType.ALLOW,
                                  },
                                })
                              }
                              id="allowRuleType"
                              key="allowRuleType"
                            >
                              {processorRulesType.ALLOW}
                            </Button>
                            <Button
                              data-cy="reviewRuleType"
                              color="info"
                              round
                              className={classes.middleButton}
                              simple={
                                values.ruleType !== processorRulesType.REVIEW
                              }
                              onClick={() =>
                                handleChange({
                                  target: {
                                    name: 'ruleType',
                                    value: processorRulesType.REVIEW,
                                  },
                                })
                              }
                              id="reviewRuleType"
                              key="reviewRuleType"
                            >
                              {processorRulesType.REVIEW}
                            </Button>
                            <Button
                              data-cy="blockRuleType"
                              color="info"
                              round
                              className={classes.lastButton}
                              simple={
                                values.ruleType !== processorRulesType.BLOCK
                              }
                              onClick={() =>
                                handleChange({
                                  target: {
                                    name: 'ruleType',
                                    value: processorRulesType.BLOCK,
                                  },
                                })
                              }
                              id="blockRuleType"
                              key="blockRuleType"
                            >
                              {processorRulesType.BLOCK}
                            </Button>
                          </div>
                          {touched.actions && Boolean(errors.ruleType) && (
                            <p className={classes.errorSpecialFields}>
                              {touched.ruleType ? errors.ruleType : ''}
                            </p>
                          )}
                        </FormControl>
                        <FormControl
                          fullWidth
                          className={classes.selectFormControl}
                        >
                          <span>{t('processor_rule.form.fields.actions')}</span>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Button
                              data-cy="insertAction"
                              color="info"
                              round
                              simple={
                                values.actions.indexOf(
                                  processorRulesActions.INSERT,
                                ) === -1
                              }
                              onClick={() => {
                                const actions =
                                  values.actions.indexOf(
                                    processorRulesActions.INSERT,
                                  ) === -1
                                    ? [
                                        ...values.actions,
                                        processorRulesActions.INSERT,
                                      ]
                                    : values.actions.filter(
                                        action =>
                                          action !==
                                          processorRulesActions.INSERT,
                                      );
                                handleChange({
                                  target: {
                                    name: 'actions',
                                    value: actions,
                                  },
                                });
                                if (values.name !== '') {
                                  const params = this.getParamsFromAction({
                                    ruleName: values.name,
                                    actionsSelected: actions,
                                    params: values.params,
                                  });
                                  handleChange({
                                    target: {
                                      name: 'params',
                                      id: 'params',
                                      value: params,
                                    },
                                  });
                                }
                              }}
                              id="insertAction"
                              key="insertAction"
                            >
                              {processorRulesActions.INSERT}
                            </Button>
                            <Button
                              data-cy="flaggedAction"
                              color="info"
                              round
                              simple={
                                values.actions.indexOf(
                                  processorRulesActions.FLAGGED,
                                ) === -1
                              }
                              onClick={() => {
                                const actions =
                                  values.actions.indexOf(
                                    processorRulesActions.FLAGGED,
                                  ) === -1
                                    ? [
                                        ...values.actions,
                                        processorRulesActions.FLAGGED,
                                      ]
                                    : values.actions.filter(
                                        action =>
                                          action !==
                                          processorRulesActions.FLAGGED,
                                      );
                                handleChange({
                                  target: {
                                    name: 'actions',
                                    value: actions,
                                  },
                                });
                                if (values.name !== '') {
                                  const params = this.getParamsFromAction({
                                    ruleName: values.name,
                                    actionsSelected: actions,
                                    params: values.params,
                                  });
                                  handleChange({
                                    target: {
                                      name: 'params',
                                      id: 'params',
                                      value: params,
                                    },
                                  });
                                }
                              }}
                              id="flaggedAction"
                              key="flaggedAction"
                            >
                              {processorRulesActions.FLAGGED}
                            </Button>
                            <Button
                              data-cy="updateAction"
                              color="info"
                              round
                              simple={
                                values.actions.indexOf(
                                  processorRulesActions.UPDATE,
                                ) === -1
                              }
                              onClick={() => {
                                const actions =
                                  values.actions.indexOf(
                                    processorRulesActions.UPDATE,
                                  ) === -1
                                    ? [
                                        ...values.actions,
                                        processorRulesActions.UPDATE,
                                      ]
                                    : values.actions.filter(
                                        action =>
                                          action !==
                                          processorRulesActions.UPDATE,
                                      );
                                handleChange({
                                  target: {
                                    name: 'actions',
                                    value: actions,
                                  },
                                });
                                if (values.name !== '') {
                                  const params = this.getParamsFromAction({
                                    ruleName: values.name,
                                    actionsSelected: actions,
                                    params: values.params,
                                  });
                                  handleChange({
                                    target: {
                                      name: 'params',
                                      id: 'params',
                                      value: params,
                                    },
                                  });
                                }
                              }}
                              id="updateAction"
                              key="updateAction"
                            >
                              {processorRulesActions.UPDATE}
                            </Button>
                          </div>
                          {touched.actions && Boolean(errors.actions) && (
                            <p className={classes.errorSpecialFields}>
                              {touched.actions ? errors.actions : ''}
                            </p>
                          )}
                        </FormControl>
                        {values.params.length > 0 &&
                          values.params[0].values.length > 0 &&
                          values.params.map(ruleParam => (
                            <FormControl
                              key={`${ruleParam.action}`}
                              fullWidth
                              className={classes.selectFormControl}
                            >
                              <span>
                                {t('processor_rule.form.params_section', {
                                  ruleAction: ruleParam.action,
                                })}
                              </span>
                              <div
                                style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                }}
                              >
                                {ruleParam.values.map(param => (
                                  <CustomInput
                                    labelText={param.key}
                                    key={`${ruleParam.action}-${param.key}`}
                                    id={`${ruleParam.action}-${param.key}`}
                                    formControlProps={{
                                      style: {
                                        marginRight: '1em',
                                        width: 'calc(100% * (1/2) - 1em)',
                                      },
                                    }}
                                    inputProps={{
                                      value: param.value,
                                      onChange: e => {
                                        const newRuleParams = values.params.map(
                                          newRuleParam => {
                                            if (
                                              newRuleParam.action ===
                                              ruleParam.action
                                            ) {
                                              const newParams = newRuleParam.values.map(
                                                ({ key, value }) => {
                                                  if (key === param.key)
                                                    return {
                                                      key,
                                                      value: e.target.value,
                                                    };
                                                  return { key, value };
                                                },
                                              );
                                              return {
                                                action: ruleParam.action,
                                                values: newParams,
                                              };
                                            }
                                            return newRuleParam;
                                          },
                                        );
                                        handleChange({
                                          target: {
                                            name: 'params',
                                            value: newRuleParams,
                                          },
                                        });
                                      },
                                      type: 'text',
                                    }}
                                  />
                                ))}
                              </div>
                            </FormControl>
                          ))}
                        {touched.params && Boolean(errors.params) && (
                          <p className={classes.errorSpecialFields}>
                            {touched.params ? errors.params : ''}
                          </p>
                        )}
                        <div className={classes.section}>
                          <GridContainer direction="row-reverse">
                            <GridItem xs={12} sm={6}>
                              <div className={classes.contentBtns}>
                                <Button
                                  id="save-processor-rule"
                                  color="primary"
                                  type="submit"
                                  style={{ float: 'right' }}
                                >
                                  {editMode
                                    ? t('processor_rule.form.edit_btn')
                                    : t('processor_rule.form.create_btn')}
                                </Button>
                              </div>
                            </GridItem>
                            <GridItem xs={12} sm={6}>
                              <Button
                                id="cancel-create-processor-rule"
                                simple
                                color="danger"
                                onClick={this.handleCancelBtn}
                                style={{ float: 'left' }}
                              >
                                {t('common.cancel')}
                              </Button>
                            </GridItem>
                          </GridContainer>
                        </div>
                      </GridItem>
                    </GridContainer>
                  </CardBody>
                </Card>
              </form>
            </GridItem>
          )}
        />
      </React.Fragment>
    );
  }
}

/**
 * ProcessorRuleForm PropTypes
 * @param classes - styles received from withStyles
 * @param processorRule - Entity data
 * @param errors - Errors data list
 * @param editMode - To active editing mode
 * @param submitLoading - To change to loading status
 * @param t - prop from HOC with string translations
 * @callback processorRuleSave - callback called when user clicks save button
 * @callback processorRuleDelete - callback called when user click delete button
 * @callback closeNotification - callback to close notification
 */

ProcessorRuleForm.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.shape({}).isRequired,
  processorRule: PropTypes.instanceOf(Object),
  errors: PropTypes.instanceOf(Array).isRequired,
  submitLoading: PropTypes.bool.isRequired,
  processorRuleSave: PropTypes.func.isRequired,
  processorRuleDelete: PropTypes.func,
  editMode: PropTypes.bool,
  closeNotification: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  routePaths: PropTypes.shape({
    LIST: PropTypes.string.isRequired,
  }),
};

ProcessorRuleForm.defaultProps = {
  processorRule: {},
  editMode: false,
  processorRuleDelete: () => {},
  routePaths: PROCESSOR_RULE,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const ProcessorRuleFormWrapper = props => <ProcessorRuleForm {...props} />;

export default withTranslation()(
  withStyles(validationFormsStyle)(ProcessorRuleFormWrapper),
);
